/**
 * LeaderboardSection.jsx
 * Landing page 3-card leaderboard display.
 * Card 1: Current/recent game standings (from game_slates/{id}/leaderboard)
 * Card 2: Top players by SS-Elo rating (from users collection)
 * Card 3: Top horses by share price in active game (from game_slates/{id}/races)
 *
 * Reads live from Firestore — no Cloud Function calls needed.
 * Shows graceful empty states until data exists.
 */

import { useState, useEffect, useRef } from 'react';

// ── Mobile scroll-highlight hook ──────────────────────────────────────────────
// Gold border applied to the card centred in the viewport on mobile only.
// Desktop :hover CSS handles the same effect — this hook is a no-op there.
function useMobileInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: '-25% 0px -25% 0px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, inView];
}
import {
  collection, query, orderBy, limit,
  onSnapshot, getDocs,
} from 'firebase/firestore';
// getDocs used by: HorseMarketCard (slate list + race reads); orderBy used by HorseMarketCard
import { db } from '../services/firebase';
import { getPublicName, getProfileImageUrl, DEFAULT_PROFILE_IMAGE } from '../utils/profileImage';
import { hydratePublicProfiles } from '../services/publicProfiles';
import './LeaderboardSection.css';

// ─── Shared card shell ────────────────────────────────────────────────────────

const LeaderCard = ({ title, icon, children, gameName }) => {
  const [cardRef, mobileInView] = useMobileInView();
  return (
    <div
      ref={cardRef}
      className={`ss-lb-card${mobileInView ? ' ss-lb-card--in-view' : ''}`}
    >
      <div className="ss-lb-card-header">
        <span className="ss-lb-card-icon">{icon}</span>
        <div>
          <h3 className="ss-lb-card-title">{title}</h3>
          {gameName && <p className="ss-lb-card-subtitle">{gameName}</p>}
        </div>
      </div>
      <div className="ss-lb-card-body">{children}</div>
    </div>
  );
};

// ─── Shared row ───────────────────────────────────────────────────────────────

const LeaderRow = ({ position, name, subName, value, isTied, avatarUrl }) => (
  <div className="ss-lb-row">
    <span className={`ss-lb-position ${position <= 3 ? 'ss-lb-position--top' : ''}`}>
      {position === 1 ? '🥇' : position === 2 ? '🥈' : position === 3 ? '🥉' : `#${position}`}
      {isTied && <span className="ss-lb-tied">T</span>}
    </span>
    {avatarUrl && (
      <img
        className="ss-lb-avatar"
        src={avatarUrl}
        alt=""
        onError={e => { e.currentTarget.src = DEFAULT_PROFILE_IMAGE; }}
      />
    )}
    <div className="ss-lb-name-block">
      <span className="ss-lb-name">{name}</span>
      {subName && <span className="ss-lb-subname">{subName}</span>}
    </div>
    <span className="ss-lb-value">{value}</span>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="ss-lb-empty">{message}</div>
);

// ─── Card 1: Current game leaderboard ────────────────────────────────────────

function GameLeaderCard() {
  const [entries,  setEntries]  = useState([]);
  const [gameName, setGameName] = useState('');
  const [loading,  setLoading]  = useState(true);
  const [profilesByUid, setProfilesByUid] = useState({});

  useEffect(() => {
    let unsubLeaderboard = null;
    let unsubSlates = null;

    // Use a one-shot onSnapshot for slates (getDocs hangs unauthenticated in prod)
    // Immediately unsubscribe after first result so it doesn't keep listening
    unsubSlates = onSnapshot(
      collection(db, 'game_slates'),
      (slatesSnap) => {
        // Unsubscribe immediately — we only need the first read
        if (unsubSlates) { unsubSlates(); unsubSlates = null; }

        const slates = slatesSnap.docs
          .map(d => ({ id: d.id, ...d.data() }));

        // For finding the active game — sort by meetingDate desc
        const slatesByDate = [...slates].sort(
          (a, b) => (b.meetingDate || '').localeCompare(a.meetingDate || '')
        );

        // For finding the last finalised game — sort by finalisedAt desc
        // so the most recently finalised game wins, not alphabetical doc ID
        const slatesByFinalised = [...slates].sort((a, b) => {
          const aMs = a.finalisedAt?.toMillis?.() ?? (a.finalisedAt?._seconds ?? 0) * 1000;
          const bMs = b.finalisedAt?.toMillis?.() ?? (b.finalisedAt?._seconds ?? 0) * 1000;
          return bMs - aMs;
        });

        const today     = new Date().toISOString().split('T')[0];
        const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

        // Priority 1: active game from today or yesterday with at least 1 race resulted
        const activeWithResults = slatesByDate.find(
          s => s.finalised !== true &&
               (s.completedRaces ?? 0) >= 1 &&
               s.meetingDate >= yesterday
        );

        // Priority 2: most recently FINALISED game that has actual results
        // Sort by finalisedAt so Pinjarra (finalised 18:49) beats Ballarat (finalised 16:59)
        // even though both have meetingDate "2026-06-24"
        const lastFinalised = slatesByFinalised.find(
          s => s.finalised === true && (s.completedRaces ?? 0) > 0
        );

        // Priority 3: any finalised game as last resort
        const anyFinalised = slatesByFinalised.find(s => s.finalised === true);

        const target = activeWithResults || lastFinalised || anyFinalised;

        if (!target) { setLoading(false); return; }

        setGameName(target.gameName || target.title || target.meetingDate || '');

        // Now subscribe to leaderboard subcollection — stays live so standings
        // update in real time as races are resulted
        unsubLeaderboard = onSnapshot(
          collection(db, 'game_slates', target.id, 'leaderboard'),
          snap => {
            const sorted = snap.docs
              .map(d => d.data())
              .sort((a, b) => (a.position ?? 99) - (b.position ?? 99))
              .slice(0, 5);
            setEntries(sorted);
            setLoading(false);
          },
          err => {
            console.error('[Card1] leaderboard error:', err.code, err.message);
            setLoading(false);
          }
        );
      },
      err => {
        console.error('[Card1] slates error:', err.code, err.message);
        setLoading(false);
      }
    );

    return () => {
      if (unsubSlates)      unsubSlates();
      if (unsubLeaderboard) unsubLeaderboard();
    };
  }, []);

  // Public identity hydration — this card is visible to signed-out visitors
  // on the landing page, so player rows must show Stable Name (username) +
  // avatar via the public player_profiles mirror, never the raw displayName
  // snapshot stored on the leaderboard doc.
  useEffect(() => {
    const missing = [...new Set(entries.map(e => e.userId))]
      .filter(uid => uid && !(uid in profilesByUid));
    if (missing.length === 0) return;
    let cancelled = false;
    hydratePublicProfiles(missing).then(map => {
      if (cancelled) return;
      setProfilesByUid(prev => ({ ...prev, ...map }));
    });
    return () => { cancelled = true; };
  }, [entries, profilesByUid]);

  return (
    <LeaderCard title="SHOWDOWN STANDINGS" icon="🏆" gameName={gameName}>
      {loading ? (
        <EmptyState message="Loading…" />
      ) : entries.length === 0 ? (
        <EmptyState message="Results pending…" />
      ) : (
        entries.map((e, i) => {
          const isTied = i > 0 && entries[i].position === entries[i - 1].position;
          const profile = profilesByUid[e.userId];
          const publicName = getPublicName(
            { ...profile, displayName: profile?.displayName || e.displayName },
            null
          );
          return (
            <LeaderRow
              key={e.userId || i}
              position={e.position ?? i + 1}
              name={publicName}
              avatarUrl={getProfileImageUrl(profile, null)}
              value={`$${(e.totalEarnings || 0).toLocaleString('en-AU')}`}
              isTied={isTied}
            />
          );
        })
      )}
    </LeaderCard>
  );
}

// ─── Card 2: Top players by SS-Elo ───────────────────────────────────────────

// Placeholder ELO data — shown until calculateEloRatings has processed
// games with 5+ players and written eloRating to user documents.
// Replace PLACEHOLDER_PLAYERS with a real Firestore query once ELO data exists.
const PLACEHOLDER_PLAYERS = [
  { name: 'Joe B',   elo: 1320 },
  { name: 'Fred S',  elo: 1185 },
  { name: 'Stacy A', elo: 1074 },
  { name: 'Donna M', elo: 952  },
  { name: 'Emma R',  elo: 840  },
];

function EloLeaderCard() {
  return (
    <LeaderCard title="PLAYER ELO RATINGS" icon="⭐">
      {PLACEHOLDER_PLAYERS.map((p, i) => (
        <LeaderRow
          key={p.name}
          position={i + 1}
          name={p.name}
          value={`${p.elo.toLocaleString('en-AU')} ELO`}
        />
      ))}
    </LeaderCard>
  );
}

// ─── Card 3: Top horses by share price ───────────────────────────────────────

function HorseMarketCard() {
  const [horses,  setHorses]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadHorses() {
      try {
        const slatesSnap = await getDocs(
          query(
            collection(db, 'game_slates'),
            orderBy('meetingDate', 'desc'),
            limit(30)
          )
        );

        const now = Date.now();
        const slates = slatesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Open slate = not finalised AND game close time is in the future
        // Excludes completed/closed games regardless of pricesLocked
        const openSlates = slates.filter(s => {
          if (s.finalised === true) return false;
          const closeMs = s.gameCloseTime?.toMillis?.() ?? 0;
          return closeMs > now;
        });

        // Fall back to most recent non-finalised slate if close time has passed
        // but game hasn't been finalised yet (same-day close window)
        const targets = openSlates.length > 0
          ? openSlates
          : slates.filter(s => s.finalised !== true).slice(0, 1);

        if (targets.length === 0) {
          setLoading(false);
          return;
        }

        const multiGame = targets.length > 1;
        const K     = 2.5;
        const GAMMA = 0.75;
        const allRunners = [];

        for (const target of targets) {
          const avgSpend     = target.avgSpendPerRace || 50000;
          const showdownName = target.gameName || target.title || target.meetingDate || '';

          const racesSnap = await getDocs(
            collection(db, 'game_slates', target.id, 'races')
          );

          for (const raceDoc of racesSnap.docs) {
            const raceData  = raceDoc.data();
            const raceLabel = raceData.raceNumber
              ? `Race ${raceData.raceNumber}`
              : (raceData.raceName || '');

            const subtitle = multiGame
              ? `${showdownName} · ${raceLabel}`
              : raceLabel;

            const runners     = raceData.runners || [];
            const processList = Array.isArray(runners) ? runners : Object.values(runners);

            processList.forEach(r => {
              if (!r) return;
              if (r.scratched || r.status?.toLowerCase() === 'scratched') return;

              const name = r.name || r.runnerName || '—';

              let sharePrice = r.sharePrice ?? r.currentPrice ?? null;
              if (sharePrice == null) {
                const odds = r.fixedOdds ?? r.odds ?? r.winOdds ?? 0;
                sharePrice = odds > 0
                  ? Math.round(K * avgSpend * Math.pow(1 / odds, GAMMA) / 100)
                  : 0;
              }

              // Only include runners with a meaningful price
              if (sharePrice > 0) {
                allRunners.push({ name, sharePrice, subtitle });
              }
            });
          }
        }

        // Sort by share price descending, take top 5
        allRunners.sort((a, b) => b.sharePrice - a.sharePrice);
        setHorses(allRunners.slice(0, 5));
      } catch (err) {
        console.error('[HorseMarketCard] error:', err);
      } finally {
        setLoading(false);
      }
    }

    loadHorses();
  }, []);

  return (
    <LeaderCard title="HOTTEST HORSE SHARES" icon="🔥">
      {loading ? (
        <EmptyState message="Loading…" />
      ) : horses.length === 0 ? (
        <EmptyState message="No active market data yet" />
      ) : (
        horses.map((h, i) => (
          <LeaderRow
            key={`${h.name}-${i}`}
            position={i + 1}
            name={h.name}
            subName={h.subtitle}
            value={`$${(h.sharePrice || 0).toLocaleString('en-AU')}`}
          />
        ))
      )}
    </LeaderCard>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function LeaderboardSection() {
  return (
    <section className="ss-lb-section" id="leaderboard">
      <div className="ss-lb-inner">
        <h2 className="ss-lb-heading">LEADERBOARDS</h2>
        <div className="ss-lb-grid">
          <GameLeaderCard />
          <EloLeaderCard />
          <HorseMarketCard />
        </div>
        {/* Horse jockey image sits below the cards */}
        <div className="ss-lb-hero-image">
          <img src="/horse-jockey.png" alt="Syndicator Showdown — Where Legends Are Forged" />
        </div>
      </div>
    </section>
  );
}
